import type { Effect, Reducer, Subscription } from '@umijs/max';
import type { SingleGoodType } from './data';
import { getGoodPage, removeGood, importGood, addGood, editGood } from './service';

export type GoodsListState = {
  code: number;
  type: string;
  message: string;
  total: number;
  current: number;
  pageSize: number;
  items: SingleGoodType[];
};

type GoodsListModelType = {
  namespace: string;
  state: GoodsListState;
  reducers: {
    GoodsListReducer: Reducer<GoodsListState>;
  };
  effects: {
    fetchGoodPageEffect: Effect;
    refreshDataEffect: Effect;
    addGoodEffect: Effect;
    editGoodEffect: Effect;
    removeGoodEffect: Effect;
    importGoodEffect: Effect;
  };
  subscriptions: {
    setup: Subscription;
  };
};

// 通用操作处理函数
const createEffect = (serviceCall: any) =>
  function* (
    { payload: action, callback }: any,
    { call, put }: any,
  ): Generator<any, void, any> {
    try {
      const res: any = yield call(
        serviceCall,
        action.values || { id: action.id },
      );

      if (res && res.code === 0) {
        yield put({
          type: 'refreshDataEffect',
          payload: { ...action },
        });
      }

      callback?.(res);
    } catch (e) {}
  };

const GoodsListModel: GoodsListModelType = {
  namespace: 'goodsList',
  state: {
    code: 0,
    type: '',
    message: 'success',
    items: [],
    total: 0,
    pageSize: 20,
    current: 1,
  },

  reducers: {
    GoodsListReducer(state, action) {
      const payload = action.payload || {};
      const items = payload.data?.items;
      return {
        ...state,
        ...payload,
        items: Array.isArray(items) ? items : [],
        total: payload.data?.total || 0,
        current: payload.data?.current || 1,
        pageSize: payload.data?.pageSize || 20,
      } as GoodsListState;
    },
  },

  effects: {
    *fetchGoodPageEffect(
      { payload: { pageSize, current, filter, sorter, with: withParam } }: any,
      { put, call }: any,
    ): Generator<any, void, any> {
      try {
        const res: any = yield call(getGoodPage, {
          pageSize,
          current,
          filter,
          sorter,
          with: withParam,
        });
        yield put({
          type: 'GoodsListReducer',
          payload: res,
        });
      } catch (e) {
        console.error('Fetch data failed:', e);
      }
    },

    // 通用刷新数据的 effect
    *refreshDataEffect(
      { payload: action }: any,
      { put, select }: any,
    ): Generator<any, void, any> {
      const current = yield select((state: any) => state.goodsList.current);
      const pageSize = yield select((state: any) => state.goodsList.pageSize);
      yield put({
        type: 'fetchGoodPageEffect',
        payload: {
          current,
          pageSize,
          filter: { ...action.filter },
          sorter: action.sorter || [],
          with: [
            'shop',
            'brand',
            'category.parent.parent',
            'imageList',
            'attrList',
            'optionList',
          ],
        },
      });
    },

    // 新增 effect
    addGoodEffect: createEffect(addGood),

    // 编辑 effect
    editGoodEffect: createEffect(editGood),

    // 删除 effect
    removeGoodEffect: createEffect(removeGood),

    // 导入商品
    *importGoodEffect(
      { payload: { file, filter, sorter }, callback }: any,
      { call, put }: any,
    ): Generator<any, void, any> {
      try {
        const res: any = yield call(importGood, file);

        if (res && res.code === 0) {
          yield put({
            type: 'refreshDataEffect',
            payload: { filter, sorter },
          });
        }

        callback?.(res);
      } catch (e) {
        console.error('Import failed:', e);
        callback?.({ code: 1, message: '导入失败' });
      }
    },
  },

  subscriptions: {
    setup() {},
  },
};

export default GoodsListModel;
